/**
 * THE CABINET'S SHELL - a header, a sidebar card, and the screen in the main region.
 *
 * WHY NOW. The first live run of the cabinet in a browser (2026-09-23) reached `/dashboard` through
 * the real hand-off and found every screen standing alone: no link from one to another and no way
 * to sign out (`./cabinet-navigation.ts` has the measurement). The doors' own frame,
 * `features/auth/components/AuthShell.tsx`, already said "IT IS NOT THE CABINET'S AppShell"; this
 * is the shell it meant.
 *
 * WHERE IT IS MOUNTED. `src/router.tsx` puts it around each screen whose descriptor belongs inside
 * the shell, and INSIDE `SessionGate`: a reader the gate is still deciding about, or is sending
 * elsewhere, sees the gate's own "Opening your cabinet" card and never a sidebar of screens that
 * would refuse them. `/`, the doors, `/terms`, `/welcome` and a rowless reader's `/apply` are out.
 *
 * ★ ITS LOOK IS THE PROTOTYPE'S (design 15, the owner's "adapt UI/styles/layouts to the prototype",
 * 2026-10-01). It was a full-height Mantine AppShell with a text list down the left edge. It is now
 * the prototype's page (`moonx-authors-prototype/MoonX App.dc.html`): a header with the MoonX mark,
 * the display settings and the author's initials; under it a centred column with a sidebar CARD -
 * who is signed in, the entries with their icons and red counts, the gold pill on the page you are
 * on, and the account links at its foot - beside the screen. The measurements and colours are in
 * ./cabinet-frame.module.css. Left out, as the owner ruled (design 15, decision 1): the prototype's
 * moon line above the header, and its links to site pages this cabinet cannot vouch for; "Your
 * public page" is offered instead, at the address the profile page already promises.
 *
 * ★ THE WAY TO THE WEBSITE IS IN THE FOOT, AND IT OPENS A NEW TAB (design 24, T10 + T14). The top
 * bar's "Back to site" pill went: on a phone it shrank to a lone arrow that read as "go back one
 * page", and the teammate asked for it in the left menu, opening the site without leaving the
 * cabinet. So the foot has "MoonX site" beside "Your public page", each opening a new tab and saying
 * so in its name - in both builds: the default one draws the foot with that one link.
 *
 * ★ THE AUTHOR'S PHOTO, NOT AN INITIAL (design 24, T13). The sidebar's mark and the top bar's
 * button show the profile's photo, read once per cabinet load rather than once per screen, with
 * the initials kept while it loads and whenever there is none to show (./account-photo.tsx).
 *
 * THE RULES IT KEEPS, each one already a rule elsewhere in this repository:
 *   - EVERY ENTRY IS A REAL `<a href>`, so a new tab, a copied address or a middle click lands on
 *     the same screen, and a plain click is handed to the router instead of reloading the page. A
 *     modified or non-primary click is left to the browser - `followsInApp`, the back office's rule
 *     (`web/admin/src/features/shell/components/ShellLink.tsx`), kept in `./in-app-navigation.ts`.
 *   - ON A PHONE THE SIDEBAR IS A PANEL THE MENU BUTTON OPENS, and a closed panel is `inert` as well
 *     as not drawn, so its links are out of the tab order and out of what a screen reader reads.
 *     The menu button says what it will do, whether the panel is open (`aria-expanded`) and which
 *     region it opens (`aria-controls`) - the back office's `NavToggle`
 *     (`web/admin/src/features/shell/components/ConsoleNavigation.tsx`).
 *   - `aria-current="page"` ON THE CURRENT ENTRY. Mantine's `active` is a colour and a `data-`
 *     attribute, which a screen reader never announces (`docs/ui/03-components.md`).
 *   - ONE TAB STOP FOR THE WHOLE LIST, with Up/Down, Home and End between entries - the rest of
 *     `:88-89`, and the same hook the back office's sidebar uses (`useRovingNav`, `@moonx/ui`). The
 *     stop is the current page's entry, else the first; clicks and Enter stay the real `<a href>`'s.
 *   - AN ATTENTION BADGE ONLY WHERE THE PLATFORM SERVES THE COUNT, with the count in the entry's
 *     accessible name - Articles and the Inbox, each from its lists' own `total`, none when a page
 *     carries none (`./cabinet-attention.ts`). The shell is mounted per screen, so the counts are
 *     asked again on every screen, and the last answer shows meanwhile, not a loading state.
 *   - ONE `<h1>` PER PAGE, and it is the screen's (`features/cabinet/components/page-header.tsx`).
 *     The mark in the header is an image with words, not a heading.
 *   - THE NAV LANDMARK IS NAMED, so it stays distinguishable from any list of links a screen draws.
 *     And the screen is in the page's one `main` - this shell's, marked `data-cabinet-shell`.
 *   - THE ACCOUNT PARTS EXIST ONLY IN A LIVE BUILD, the back office's rule
 *     (`web/admin/src/features/shell/components/SessionChrome.tsx`): the default build has no
 *     session to show and nothing to sign out of, and a control that can only fail is worse than
 *     no control. The sidebar itself is in both builds, and so is its foot's "MoonX site", which
 *     needs no session - the accessibility leg checks the default one, so the chrome every author
 *     will see is the chrome that gets measured.
 *
 * ITS WORDS FOLLOW THE CABINET'S LANGUAGE (design 29, W5), from the `shell` catalogue - the sidebar's
 * entries too, by address (`navTextKeyOf`). And in a live build the shell takes step 2 of the
 * language order: an author who never chose a language in this browser starts in their profile's
 * (`ProfileLanguage`, at the foot; ./cabinet-language.ts says the order).
 *
 * SIGNING OUT is local, as the provider says of itself ("the cabinet holds no server-side session
 * and this surface has no sign-out route", `../auth/session/AuthSessionProvider.tsx`). Once the SDK
 * has let go, the reader is moved to `/sign-in` with a `replace`, like every move the gate makes.
 */
import {
  Burger,
  NavLink,
  Stack,
  Text,
  em,
  getBreakpointValue,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
  BadgeCheck,
  ExternalLink,
  LogOut,
  navAttentionSection,
  navEntryName,
  useNavAttention,
  useRovingNav,
} from '@moonx/ui';
import logoUrl from '../../assets/moonx-logo.png';
import { isLiveApiConfigured } from '../../config';
import { useTranslation } from '../../i18n';
import { CABINET_HOME_PATH, SIGN_IN_PATH } from '../auth/navigation';
import { WEBSITE_ORIGIN } from '../auth/handoff/website-handoff';
import { useAuthSession } from '../auth/session/AuthSessionProvider';
import { cabinetIsReachable } from '../auth/session/session-state';
import { useCabinetApi } from '../cabinet/api';
import { AccountPhoto, ownProfileOnce } from './account-photo';
import { CABINET_ATTENTION_WORDS, cabinetAttention } from './cabinet-attention';
import { adoptProfileLanguage, savedCabinetLanguage } from './cabinet-language';
import {
  CABINET_NAV_ENTRIES,
  PROFILE_NAV_ENTRY,
  currentNavPath,
  navEntriesFor,
  navTextKeyOf,
} from './cabinet-navigation';
import type { CabinetNavEntry } from './cabinet-navigation';
import { DisplaySettings } from './DisplaySettings';
import { followsInApp } from './in-app-navigation';
import { navIconFor } from './nav-icons';
import classes from './cabinet-frame.module.css';

/**
 * What the sidebar's `<nav>` is announced as in English - `frame.navLabel` in the catalogue -
 * exported for the specs that find it by name.
 */
export const CABINET_NAV_LABEL = 'Cabinet';

/** What the auth layer calls a magic-link account (`features/auth/firebase/firebase-auth-client.ts`). */
const EMAIL_LINK_PROVIDER = 'E-mail link';

/** The sidebar's `id`, which the menu button names in `aria-controls`. One shell per page. */
export const CABINET_NAVBAR_ID = 'cabinet-navbar';

/** Where the sidebar becomes a panel the menu button opens - Mantine's `sm`, stated once. */
const DRAWER_BREAKPOINT = 'sm';

/**
 * True while the sidebar is a CLOSED PANEL: the window is narrower than the breakpoint and the
 * panel is not open. The query is the one the stylesheet hides the panel by - `max-width` a tenth
 * of a pixel under the breakpoint (./cabinet-frame.module.css, `47.99em`) - so this is true exactly
 * when the links are not drawn.
 */
function useDrawerOffscreen(opened: boolean): boolean {
  const theme = useMantineTheme();
  const narrow = useMediaQuery(
    `(max-width: ${em(getBreakpointValue(DRAWER_BREAKPOINT, theme.breakpoints) - 0.1)})`,
  );
  return narrow === true && !opened;
}

/** A plain click on an in-app link, handed to the router; anything else is the browser's. */
function useFollowInApp(onFollow?: () => void) {
  const router = useRouter();
  return useCallback(
    (event: MouseEvent<HTMLElement>, path: string) => {
      if (!followsInApp(event)) {
        return;
      }
      event.preventDefault();
      onFollow?.();
      router.history.push(path);
    },
    [onFollow, router],
  );
}

export function CabinetShell({ children }: { readonly children: ReactNode }) {
  const [opened, { toggle, close }] = useDisclosure();
  // The editor folds the sidebar away until the menu button opens it (`useWritingPage`, the foot).
  const writing = useWritingPage();
  const folded = writing && !opened;
  const offscreen = useDrawerOffscreen(opened) || folded;
  const { t } = useTranslation('shell');

  return (
    <div className={classes.frame}>
      <div className={classes.sky} aria-hidden="true" />
      {isLiveApiConfigured() ? <ProfileLanguage /> : null}
      <CabinetHeader opened={opened} onToggle={toggle} menuAlways={writing} />

      <div className={classes.body} data-folded={folded ? '' : undefined}>
        <nav
          id={CABINET_NAVBAR_ID}
          aria-label={t('frame.navLabel')}
          inert={offscreen}
          className={`${classes.sidebar}${opened ? '' : ` ${classes.sidebarClosed}`}`}
        >
          <AccountIdentity />
          {/* On a phone the sidebar is a panel over the screen; following an entry closes it. */}
          <CabinetNavigation onFollow={close} />
          <AccountFoot />
        </nav>

        <main className={classes.main} data-cabinet-shell="">
          {children}
        </main>
      </div>
    </div>
  );
}

function CabinetHeader({
  opened,
  onToggle,
  menuAlways,
}: {
  readonly opened: boolean;
  readonly onToggle: () => void;
  readonly menuAlways: boolean;
}) {
  const follow = useFollowInApp();
  const { t } = useTranslation('shell');
  return (
    <header className={classes.header}>
      <div className={classes.headerInner}>
        <div className={classes.headerActions}>
          <Burger
            opened={opened}
            onClick={onToggle}
            {...(menuAlways ? {} : { hiddenFrom: DRAWER_BREAKPOINT })}
            size="sm"
            aria-label={opened ? t('frame.closeMenu') : t('frame.openMenu')}
            aria-expanded={opened}
            aria-controls={CABINET_NAVBAR_ID}
          />
          <a
            href={CABINET_HOME_PATH}
            className={classes.brand}
            onClick={(event) => {
              follow(event, CABINET_HOME_PATH);
            }}
          >
            <img src={logoUrl} alt={t('frame.logoAlt')} className={classes.logo} />
          </a>
        </div>
        <div className={classes.headerActions}>
          <DisplaySettings />
          <AccountInitials />
        </div>
      </div>
    </header>
  );
}

/**
 * The entries, decided per build like every live-only branch here (`isLiveApiConfigured()` never
 * changes between renders, so each branch keeps its own hook order).
 */
function CabinetNavigation({ onFollow }: { readonly onFollow: () => void }) {
  return isLiveApiConfigured() ? (
    <LiveCabinetNavigation onFollow={onFollow} />
  ) : (
    <NavigationList entries={CABINET_NAV_ENTRIES} identity="" onFollow={onFollow} />
  );
}

function LiveCabinetNavigation({ onFollow }: { readonly onFollow: () => void }) {
  const { state } = useAuthSession();
  return (
    <NavigationList
      entries={navEntriesFor(cabinetIsReachable(state))}
      identity={state.identity?.uid ?? ''}
      onFollow={onFollow}
    />
  );
}

/**
 * `identity` is whose counts the badges show: one client serves every author who signs in on this
 * tab, so a remembered count is kept per person and never shown to the next one.
 */
function NavigationList({
  entries,
  identity,
  onFollow,
}: {
  readonly entries: readonly CabinetNavEntry[];
  readonly identity: string;
  readonly onFollow: () => void;
}) {
  const follow = useFollowInApp(onFollow);
  const { t } = useTranslation('shell');
  const api = useCabinetApi();
  const pathname = useLocation({ select: (location) => location.pathname });
  const current = currentNavPath(entries, pathname);
  const roving = useRovingNav({
    count: entries.length,
    currentIndex: entries.findIndex((entry) => entry.path === current),
  });
  const counts = cabinetAttention(api);
  // Asked once per mount: `src/router.tsx` mounts the shell around each screen, so every screen
  // asks afresh. A key on the address would add a read the outgoing shell makes as it leaves.
  const attention = useNavAttention(
    new Map([...counts].filter(([path]) => entries.some((entry) => entry.path === path))),
    { scope: api, identity, refreshKey: '' },
  );

  return (
    <Stack gap={2}>
      {entries.map((entry, index) => {
        const active = entry.path === current;
        const { ref, ...keyboard } = roving(index);
        const says = CABINET_ATTENTION_WORDS.get(entry.path);
        const state = attention.get(entry.path);
        const Icon = navIconFor(entry.path);
        const key = navTextKeyOf(entry);
        const label = key === null ? entry.label : t(key);
        return (
          <NavLink
            key={entry.path}
            ref={ref}
            {...keyboard}
            label={label}
            href={entry.path}
            active={active}
            className={classes.navLink}
            classNames={{ label: classes.navLabel }}
            leftSection={Icon === undefined ? undefined : <Icon size={16} aria-hidden />}
            aria-current={active ? 'page' : undefined}
            aria-label={says === undefined ? undefined : navEntryName(label, state, says)}
            rightSection={navAttentionSection(state)}
            onClick={(event) => {
              follow(event, entry.path);
            }}
          />
        );
      })}
    </Stack>
  );
}

/* ================================================== the account parts - a live build only */

function AccountIdentity() {
  return isLiveApiConfigured() ? <LiveAccountIdentity /> : null;
}

/**
 * The signed-in author at the top of the sidebar - the "Sidebar identity" row of
 * `docs/ui/04-pages.md`, drawn as the prototype's card head: a round mark with their photo,
 * their name, their public handle, and "Verified author" once their application is approved. The
 * photo is read once per cabinet load, not per screen, and the mark keeps their initial while it
 * loads and whenever there is none to show (./account-photo.tsx, design 24 T13).
 */
function LiveAccountIdentity() {
  const { state } = useAuthSession();
  const { t } = useTranslation('shell');
  const name = state.session?.displayName ?? null;
  const slug = state.session?.slug ?? null;
  if (name === null && slug === null) {
    return null;
  }
  return (
    <div className={classes.identity}>
      <div className={classes.avatar} aria-hidden="true">
        <AccountPhoto initials={initialOf(name ?? slug ?? '')} />
      </div>
      {name === null ? null : (
        <Text size="sm" fw={800}>
          {name}
        </Text>
      )}
      {slug === null ? null : (
        <Text size="xs" c="dimmed">
          @{slug}
        </Text>
      )}
      {state.session?.authorStatus === 'approved' ? (
        <span className={classes.verified}>
          <BadgeCheck size={12} className={classes.verifiedIcon} aria-hidden />
          {t('frame.verified')}
        </span>
      ) : null}
    </div>
  );
}

/** The header's round photo (or initials), leading to the author's profile. */
function AccountInitials() {
  return isLiveApiConfigured() ? <LiveAccountInitials /> : null;
}

function LiveAccountInitials() {
  const { state } = useAuthSession();
  const follow = useFollowInApp();
  const { t } = useTranslation('shell');
  const name = state.session?.displayName ?? state.identity?.displayName ?? null;
  if (name === null || name.trim().length === 0) {
    return null;
  }
  return (
    <a
      href={PROFILE_NAV_ENTRY.path}
      className={`${classes.roundControl} ${classes.initials}`}
      aria-label={t('frame.profileButton', { name })}
      onClick={(event) => {
        follow(event, PROFILE_NAV_ENTRY.path);
      }}
    >
      <AccountPhoto initials={initialsOf(name)} />
    </a>
  );
}

/** The sidebar's foot: the public page, the MoonX site, how they signed in, and signing out. */
function AccountFoot() {
  return isLiveApiConfigured() ? (
    <LiveAccountFoot />
  ) : (
    <div className={classes.sidebarFoot}>
      <SiteLink />
    </div>
  );
}

function LiveAccountFoot() {
  const { state, signOut } = useAuthSession();
  const router = useRouter();
  const { t } = useTranslation('shell');
  const [leaving, setLeaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // Nothing public before approval (design 25, D6): an applicant's reserved address has no page.
  const slug = state.session?.authorStatus === 'applicant' ? null : (state.session?.slug ?? null);
  const provider = state.identity?.providerLabels[0] ?? null;
  // The auth layer's names are data - the payout re-sign-in matches them - so they are put into
  // words here: Apple and Google are names in every language, the e-mail link is not.
  const providerWords = provider === EMAIL_LINK_PROVIDER ? t('frame.emailLink') : provider;

  const leave = useCallback(async () => {
    setLeaving(true);
    setFailed(false);
    try {
      await signOut();
      router.history.replace(SIGN_IN_PATH);
    } catch {
      // The SDK kept the session. Say so and leave the button usable; the reader is still in.
      setFailed(true);
      setLeaving(false);
    }
  }, [router, signOut]);

  return (
    <div className={classes.sidebarFoot}>
      {slug === null ? null : (
        <a
          href={`${WEBSITE_ORIGIN}/authors/${encodeURIComponent(slug)}`}
          className={classes.footLink}
          target="_blank"
          rel="noopener"
          aria-label={t('frame.inNewTab', { words: t('frame.publicPage') })}
        >
          <ExternalLink size={15} aria-hidden />
          {t('frame.publicPage')}
        </a>
      )}
      <SiteLink />
      {providerWords === null ? null : (
        <div className={classes.footNote}>
          {t('frame.signedInWith', { provider: providerWords })}
        </div>
      )}
      <button
        type="button"
        className={classes.footLink}
        disabled={leaving}
        onClick={() => {
          void leave();
        }}
      >
        <LogOut size={15} aria-hidden />
        {t('frame.signOut')}
      </button>
      {failed ? (
        <Text size="xs" role="alert" px={12}>
          {t('frame.signOutFailed')}
        </Text>
      ) : null}
    </div>
  );
}

/**
 * "MoonX site": the website, in a NEW TAB, so the cabinet stays open behind it (design 24, T10 +
 * T14). It needs no session, so it is the one part of the foot the default build draws too.
 */
function SiteLink() {
  const { t } = useTranslation('shell');
  return (
    <a
      href={WEBSITE_ORIGIN}
      className={classes.footLink}
      target="_blank"
      rel="noopener"
      // A new-tab link's name: its words, then what it does, so the new tab surprises nobody.
      aria-label={t('frame.inNewTab', { words: t('frame.site') })}
    >
      <ExternalLink size={15} aria-hidden />
      {t('frame.site')}
    </a>
  );
}

function initialOf(name: string): string {
  return name.trim().charAt(0).toLocaleUpperCase() || '·';
}

/** "Mira Halloway" -> "MH"; one word -> its first letter. */
function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toLocaleUpperCase();
}

/* ============================================================ the editor, drawn full width */

/**
 * The editors' addresses: an article's, `/articles/$articleId/edit` (features/cabinet/routes.ts),
 * and a video's, `/videos/$articleId/edit` (`VIDEO_EDITOR_ROUTE_PATH`, routes/videos/paths.ts).
 */
const WRITING_PAGE = /^\/(?:articles|videos)\/[^/]+\/edit\/?$/;

/**
 * ★ ON THE EDITOR THE SIDEBAR IS FOLDED AWAY (decision-log row 247's judgement call (c), taken as
 * recommended on 2026-10-05 - "fix all this small issues"). The prototype draws its editor as a
 * page of its own, the full width under the header with a way back above it
 * (`moonx-authors-prototype/MoonX App.dc.html`, `isEditor`): the writing canvas and the tabbed
 * panel beside it need the room the sidebar card took, and the editor already says where it is
 * and how to leave ("Articles" in its own line of crumbs).
 *
 * FOLDED, NOT REMOVED. The sidebar is still in the page, as the closed panel a phone has: the menu
 * button is shown at every width here, a closed panel is `inert` as well as not drawn, and opening
 * it puts the page back into its two columns until an entry is followed. So the rest of the
 * cabinet is one press away, the same press on every screen size, and nothing the sidebar offers
 * (the counts, the account parts) is lost. ./cabinet-frame.module.css, `[data-folded]`, draws it.
 */
function useWritingPage(): boolean {
  return useLocation({ select: (location) => WRITING_PAGE.test(location.pathname) });
}

/* ================================================ the profile's language, read once (live) */

/**
 * Step 2 of the language order (./cabinet-language.ts): an author who never chose a language in
 * this browser starts in their profile's `languageCode`, which is then kept as this browser's choice.
 * The profile is the one the photo reads (`ownProfileOnce`), so this sends no request of its own,
 * and it is only ever read. Nothing is drawn.
 */
function ProfileLanguage() {
  const api = useCabinetApi();
  const { state } = useAuthSession();
  const identity = state.identity?.uid ?? '';
  useEffect(() => {
    if (savedCabinetLanguage() !== null) {
      return;
    }
    void ownProfileOnce(api, identity).then((profile) =>
      adoptProfileLanguage(profile?.languageCode),
    );
  }, [api, identity]);
  return null;
}
